/**
 * @file
 * Model-driven forms. Tracks the values and statuses of inputs.
 * Works well with React.memo, by re-using results when possible.
 *
 * Built on the incremental computation primitives in `@redotech/util/inc`.
 * The two core shapes:
 *
 * - {@link Input} is the *state* of a field: its current `value`, whether it
 *   has `changed`, its validation `errors` (and `allErrors`, including
 *   descendants), and a `setValue` callback.
 * - {@link InputSchema} is the *schema*: given an `initial` value and a
 *   `setValue` updater, it returns an `IncFunction` mapping the current value
 *   to its `Input` state.
 *
 * Providers compose structurally — {@link groupInput} (object),
 * {@link listInput} (array), and {@link optionalInput} (nullable) build a
 * provider tree whose value mirrors the model's shape. Each combinator pairs an
 * `inc` structural step (`incObject`/`incArray`/`incOptional`) with an
 * `incCache` projection into `Input`, so editing one field reuses the unchanged
 * siblings' state.
 */

import { Equal, identityEqual } from "@redotech/util/equal";
import {
  ArrayKeyFn,
  IncFunction,
  incArray,
  incCache,
  incCompose,
  incObject,
  incOptional,
} from "@redotech/util/inc";
import { objectMapValues } from "@redotech/util/object";
import { ValidationError, Validator, noopValidator } from "./validator";

/**
 * Input state
 * @template Value Value
 */
export interface Input<Value> {
  /** Whether input has changed from initial value */
  changed: boolean;
  /** Value */
  value: Value;
  /** Error for this input and child inputs */
  allErrors: ValidationError[];
  /** Errors for this input */
  errors: ValidationError[];
  /** Change the value */
  setValue(value: Value): void;
}

export type InputValue<T> = T extends Input<infer Value> ? Value : never;

/**
 * Input schema
 */
/**
 * @param initial Initial value
 * @param setValue Set value
 */
export type InputSchema<Value, Input_ extends Input<Value> = Input<Value>> = (
  initial: Value,
  setValue: SetState<Value>,
) => IncFunction<Value, Input_>;

/**
 * Input schema
 */
export namespace InputSchema {
  export type Input<T extends InputSchema<any, any>> =
    T extends InputSchema<any, infer Input> ? Input : never;
  export type Value<T extends InputSchema<any, any>> =
    T extends InputSchema<infer Value, any> ? Value : never;
}

/**
 * Optional input state
 */
export interface OptionalInput<
  Value,
  InnerInput extends Input<Value>,
> extends Input<Value | undefined> {
  /** Child input */
  input: InnerInput | undefined;
}

/**
 * Optional input. If the value is undefined, the input (and validation) is disabled.
 * @param inner Input
 * @param initialFn Value factory for initial value.
 */
export function optionalInput<Value, InnerInput extends Input<Value>>(
  inner: InputSchema<Value, InnerInput>,
  initialFn: () => Value,
): InputSchema<Value | undefined, OptionalInput<Value, InnerInput>> {
  return (initial, setValue) => {
    const empty: OptionalInput<Value, InnerInput> = {
      changed: initial !== undefined,
      allErrors: [],
      input: undefined,
      errors: [],
      value: undefined,
      setValue: (value) => setValue(() => value),
    };

    return incCompose(
      incOptional(
        inner(initial !== undefined ? initial : initialFn(), (value) =>
          setValue((v) => value(v !== undefined ? v : initialFn())),
        ),
      ),
      incCache((input) =>
        input !== undefined
          ? {
              changed: input.changed,
              allErrors: input.allErrors,
              errors: input.errors,
              value: input.value,
              setValue: (value) => setValue(() => value),
              input,
            }
          : empty,
      ),
    );
  };
}

/**
 * Group input state
 * @template Inputs Sub-inputs
 */
export interface GroupInput<Inputs> extends Input<{
  [K in keyof Inputs]: InputValue<Inputs[K]>;
}> {
  /** Child inputs */
  inputs: Inputs;
}

/**
 * Group input
 */
export function groupInput<C>(
  inputs: {
    [K in keyof C]: C[K] extends Input<infer V> ? InputSchema<V, C[K]> : never;
  },
  {
    validator = noopValidator,
  }: { validator?: Validator<{ [K in keyof C]: InputValue<C[K]> }> } = {},
): InputSchema<{ [K in keyof C]: InputValue<C[K]> }, GroupInput<C>> {
  return (initial, setValue): IncFunction<any, any> => {
    const controls = objectMapValues(inputs, (provider, key) =>
      provider(initial[key as keyof C], (value) => {
        setValue((values) => ({ ...values, [key]: value(values[key]) as any }));
      }),
    ) as { [K in keyof C]: IncFunction<any, Input<any>> };
    return incCompose(
      incObject(controls),
      incCache((inputs): GroupInput<C> => {
        const value = objectMapValues(
          inputs,
          (input) => (input as Input<any>).value,
        ) as { [K in keyof C]: InputValue<C[K]> };
        const errors = validator(value);
        return {
          errors,
          allErrors: [
            ...Object.values(inputs).flatMap(
              (input) => (input as Input<any>).allErrors,
            ),
            ...errors,
          ],
          changed: Object.values(inputs).some(
            (input) => (input as Input<any>).changed,
          ),
          setValue: (value) => setValue(() => value),
          inputs: inputs as C,
          value,
        };
      }),
    );
  };
}

/**
 * Single-value input
 */
export function input<T>({
  equal = identityEqual,
  validator = noopValidator,
  onlyValidateOnChange = false,
}: {
  equal?: Equal<T>;
  validator?: Validator<T>;
  onlyValidateOnChange?: boolean;
} = {}): InputSchema<T> {
  return (initial, setValue) =>
    incCache<T, Input<T>>((value) => {
      const changed = !equal(initial, value);
      const errors = onlyValidateOnChange && !changed ? [] : validator(value);
      return {
        changed,
        allErrors: errors,
        errors,
        value,
        setValue: (value) => setValue(() => value),
      };
    }, equal);
}

/**
 * List input state
 */
export interface ListInput<
  ElementValue,
  ElementInput extends Input<ElementValue>,
> extends Input<ElementValue[]> {
  /** Child inputs */
  inputs: ElementInput[];
}

/**
 * Dynamically-sized list of inputs
 */
export function listInput<K, V, C extends Input<V>>(
  fn: (key: K) => InputSchema<V, C>,
  initialFn: (key: K) => V,
  keyFn: ArrayKeyFn<V, K>,
  { validator = noopValidator }: { validator?: Validator<V[]> } = {},
): InputSchema<V[], ListInput<V, C>> {
  return (initial, setValue) => {
    const keyedInitial = new Map(
      initial.map((value, index) => [keyFn(value, index), value]),
    );
    return incCompose(
      incArray(
        (key) =>
          fn(key)(
            keyedInitial.has(key) ? keyedInitial.get(key)! : initialFn(key),
            (value) =>
              setValue((values) =>
                values.map((v, index) =>
                  Object.is(key, keyFn(v, index)) ? value(v) : v,
                ),
              ),
          ),
        keyFn,
      ),
      incCache((inputs) => {
        const value = inputs.map((input) => input.value);
        const changed =
          initial.length !== inputs.length ||
          inputs.some(
            (input, index) =>
              input.changed ||
              !Object.is(
                // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                keyFn(initial[index], index),
                keyFn(input.value, index),
              ),
          );
        const errors = validator(value);
        return {
          changed,
          allErrors: [...inputs.flatMap((input) => input.allErrors), ...errors],
          errors,
          setValue: (value) => setValue(() => value),
          inputs,
          value,
        };
      }),
    );
  };
}

/**
 * Set state action
 */
export type SetState<T> = (fn: (existing: T) => T) => void;
